/**
 * Shared element spacing controls for glass panels and spacing badges.
 *
 * Phase 2: replaces the 8-input grid with a Figma-style box-model widget,
 * adds a number-scrubber drag with iframe-zoom math + design-token snap,
 * a polished Apply-to dropdown with hover-preview/count/dim, mixed-state
 * diversity readout, post-commit toast, first-session coachmark, keyboard
 * map (Esc cancels drag, arrow keys nudge), commit micro-animations,
 * and a transient drag-mode CSS bypass for >5 sibling targets.
 */

import { store } from '../core/state.js';
import { eventBus } from '../core/events.js';
import { bridge } from './bridge.js';
import {
  applyElementOverrides,
  getElementOverridesForViewport,
  resolveSectionByInstance
} from './section-utils.js';
import { getActiveViewportName, getResponsiveOverrideViewport } from '../ui/viewport-controls.js';
import { BoxModelWidget } from './box-model-widget.js';
import { renderBreadcrumb } from './selection-breadcrumb.js';
import { beginScrub, readDesignTokenLadder, nextTokenStep } from './value-scrubber.js';

// Toast helper is lazy-loaded so this module stays import-safe in Node test
// runners that don't pull in the full UI shell.
let _undoToastFn = null;
async function _ensureUndoToast() {
  if (_undoToastFn) return _undoToastFn;
  try {
    const mod = await import('../ui/shell.js');
    _undoToastFn = mod.showUndoToast || (() => {});
  } catch {
    _undoToastFn = () => {};
  }
  return _undoToastFn;
}

export const SPACING_FIELDS = Object.freeze([
  'paddingTop',
  'paddingRight',
  'paddingBottom',
  'paddingLeft',
  'marginTop',
  'marginRight',
  'marginBottom',
  'marginLeft'
]);

export const PICTURE_SPACING_PROPS = Object.freeze([
  'picturePaddingTop',
  'picturePaddingRight',
  'picturePaddingBottom',
  'picturePaddingLeft',
  'pictureMarginTop',
  'pictureMarginRight',
  'pictureMarginBottom',
  'pictureMarginLeft'
]);

const TRANSIENT_CSS_THRESHOLD = 5;
const COACHMARK_KEY = 'pb.coachmark.spacing.v1';

const FIELD_AXIS = Object.freeze({
  paddingTop: 'y',
  paddingBottom: 'y',
  marginTop: 'y',
  marginBottom: 'y',
  paddingLeft: 'x',
  paddingRight: 'x',
  marginLeft: 'x',
  marginRight: 'x'
});

const FIELD_DRAG_SIGN = Object.freeze({
  paddingTop: 1,
  paddingBottom: 1,
  paddingLeft: 1,
  paddingRight: 1,
  marginTop: -1,
  marginBottom: 1,
  marginLeft: -1,
  marginRight: 1
});

const FIELD_TO_CSS = Object.freeze({
  paddingTop: 'padding-top',
  paddingRight: 'padding-right',
  paddingBottom: 'padding-bottom',
  paddingLeft: 'padding-left',
  marginTop: 'margin-top',
  marginRight: 'margin-right',
  marginBottom: 'margin-bottom',
  marginLeft: 'margin-left'
});

function cap(value) {
  return (
    String(value || '')
      .charAt(0)
      .toUpperCase() + String(value || '').slice(1)
  );
}

function isImageInsidePicture(data) {
  return data?.editableType === 'image' && !!data.pictureRect;
}

export function routeSpacingProp(field, data = {}) {
  if (!SPACING_FIELDS.includes(field)) return field;
  return isImageInsidePicture(data) ? `picture${cap(field)}` : field;
}

export function isSpacingOverrideProp(prop) {
  return SPACING_FIELDS.includes(prop) || PICTURE_SPACING_PROPS.includes(prop);
}

export function spacingFieldForProp(prop) {
  if (SPACING_FIELDS.includes(prop)) return prop;
  if (typeof prop === 'string' && prop.startsWith('picture')) {
    const raw = prop.slice('picture'.length);
    return raw ? raw.charAt(0).toLowerCase() + raw.slice(1) : prop;
  }
  return prop;
}

export function spacingForDisplayData(data = {}) {
  if (isImageInsidePicture(data) && data.picturePositioning) {
    return data.picturePositioning;
  }
  return data.spacing || {};
}

export function mergeSpacingSnapshotData(data = {}, snapshot = {}) {
  const next = { ...data };
  if (snapshot.spacing && typeof snapshot.spacing === 'object') {
    next.spacing = { ...(data.spacing || {}), ...snapshot.spacing };
  }
  if (snapshot.spacingTemplate && typeof snapshot.spacingTemplate === 'object') {
    next.spacingTemplate = { ...(data.spacingTemplate || {}), ...snapshot.spacingTemplate };
  }
  if (snapshot.pictureSpacing && typeof snapshot.pictureSpacing === 'object') {
    next.picturePositioning = { ...(data.picturePositioning || {}), ...snapshot.pictureSpacing };
  }
  if (snapshot.own?.rootFontSize) next.computedRootFontSize = snapshot.own.rootFontSize;
  return next;
}

export function rootFontPx(data = {}) {
  const parsed = Number.parseFloat(data.computedRootFontSize || '');
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 16;
}

export function parseCssLengthPx(value, rootPx = 16) {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const raw = String(value || '')
    .trim()
    .toLowerCase();
  if (!raw || raw === 'auto') return 0;
  const num = Number.parseFloat(raw);
  if (!Number.isFinite(num)) return 0;
  if (raw.endsWith('rem')) return num * rootPx;
  if (raw.endsWith('em')) return num * rootPx;
  return num;
}

export function formatCssLength(px, unit = 'rem', rootPx = 16) {
  const safePx = Number.isFinite(px) ? px : 0;
  if (unit === 'px') return `${Math.round(safePx)}px`;
  const rem = safePx / (rootPx || 16);
  const rounded = Math.round(rem * 1000) / 1000;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : String(rounded).replace(/0+$/, '').replace(/\.$/, '')}rem`;
}

export function formatInputValue(value, unit = 'rem', rootPx = 16) {
  const px = parseCssLengthPx(value, rootPx);
  if (unit === 'px') return String(Math.round(px));
  const rem = px / (rootPx || 16);
  const rounded = Math.round(rem * 1000) / 1000;
  return Number.isInteger(rounded)
    ? String(rounded)
    : String(rounded).replace(/0+$/, '').replace(/\.$/, '');
}

export function parseInputCssValue(value, unit = 'rem') {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (raw.toLowerCase() === 'auto') return 'auto';
  if (/^-?\d*\.?\d+(px|rem|em|%)$/i.test(raw)) return raw;
  const num = Number.parseFloat(raw);
  if (!Number.isFinite(num)) return '';
  return `${num}${unit}`;
}

export function hasMixedSpacingValue(items, field, rootPx = 16) {
  if (!Array.isArray(items) || items.length < 2) return false;
  let first = null;
  for (const item of items) {
    const computed = item?.computed || {};
    const px = Math.round(parseCssLengthPx(computed[field], rootPx) * 100) / 100;
    if (first === null) {
      first = px;
    } else if (Math.abs(px - first) > 0.5) {
      return true;
    }
  }
  return false;
}

export function detectMixedSpacingFields(items, fields = SPACING_FIELDS, rootPx = 16) {
  const out = {};
  for (const field of fields) {
    out[field] = hasMixedSpacingValue(items, field, rootPx);
  }
  return out;
}

export function diversityForField(items, field, rootPx = 16) {
  if (!Array.isArray(items) || items.length === 0) return [];
  const seen = new Map();
  for (const item of items) {
    const value = item?.computed?.[field];
    if (value == null) continue;
    const px = Math.round(parseCssLengthPx(value, rootPx));
    if (!seen.has(px)) seen.set(px, `${px}px`);
  }
  return Array.from(seen.values());
}

export function createSpacingElementsUpdate(elements, targetEids, prop, value) {
  const next = { ...(elements || {}) };
  for (const eid of targetEids || []) {
    if (!eid) continue;
    const elOverrides = { ...(next[eid] || {}) };
    if (value) elOverrides[prop] = value;
    else delete elOverrides[prop];
    if (Object.keys(elOverrides).length > 0) next[eid] = elOverrides;
    else delete next[eid];
  }
  return next;
}

const defaultRequestFrame = (callback) => {
  if (typeof requestAnimationFrame === 'function') return requestAnimationFrame(callback);
  return setTimeout(callback, 16);
};

const defaultCancelFrame = (id) => {
  if (typeof cancelAnimationFrame === 'function') return cancelAnimationFrame(id);
  return clearTimeout(id);
};

export function createFrameBatcher(
  writeFn,
  requestFrame = defaultRequestFrame,
  cancelFrame = defaultCancelFrame
) {
  let frameId = null;
  let pending = null;
  const flush = () => {
    if (frameId !== null && typeof cancelFrame === 'function') {
      cancelFrame(frameId);
    }
    frameId = null;
    if (pending !== null) {
      const next = pending;
      pending = null;
      writeFn(next);
    }
  };
  return {
    schedule(payload) {
      pending = payload;
      if (frameId !== null) return;
      frameId = requestFrame(() => {
        frameId = null;
        const next = pending;
        pending = null;
        if (next !== null) writeFn(next);
      });
    },
    flush,
    cancel() {
      if (frameId !== null) cancelFrame(frameId);
      frameId = null;
      pending = null;
    }
  };
}

function createEl(tag, className, text = '') {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (text) el.textContent = text;
  return el;
}

function sectionElementOverrides(instanceId) {
  const resolved = resolveSectionByInstance(instanceId);
  const section = resolved?.section;
  if (!section) return {};
  const eids = new Set([
    ...Object.keys(section.overrides?.elements || {}),
    ...Object.keys(section.overrides?.responsive?.[getActiveViewportName()]?.elements || {})
  ]);
  return Object.fromEntries(
    [...eids].map((eid) => [
      eid,
      getElementOverridesForViewport(section, eid, getActiveViewportName())
    ])
  );
}

function currentSpacingItemFromRect(data) {
  if (!data?.eid) return null;
  return {
    eid: data.eid,
    rect: isImageInsidePicture(data) && data.pictureRect ? data.pictureRect : data.rect,
    computed: spacingForDisplayData(data),
    template: data.spacingTemplate || {},
    override: sectionElementOverrides(data.instanceId)[data.eid] || {}
  };
}

function isTemplateValue(value) {
  if (typeof value !== 'string') return false;
  const lower = value.toLowerCase();
  return (
    lower.includes('clamp(') ||
    lower.includes('vw') ||
    lower.includes('vh') ||
    lower.includes('calc(') ||
    lower.includes('min(') ||
    lower.includes('max(') ||
    lower.includes('var(') ||
    lower.endsWith('%')
  );
}

function readCoachmarkSeen() {
  try {
    return localStorage.getItem(COACHMARK_KEY) === '1';
  } catch {
    return true;
  }
}

function writeCoachmarkSeen() {
  try {
    localStorage.setItem(COACHMARK_KEY, '1');
  } catch {}
}

export function mountSharedSpacingControls({ panel, container, data }) {
  if (!container || !data?.eid) return null;
  const controller = new SharedSpacingControls(panel, container, data);
  controller.mount();
  return controller;
}

class SharedSpacingControls {
  constructor(panel, container, data) {
    this._panel = panel;
    this._container = container;
    this._data = data;
    this._rectData = data;
    this._unit = 'rem';
    this._scope = 'self';
    this._scopeHover = null;
    this._groupItems = [];
    this._groupItemsCache = [];
    this._groupAvailable = false;
    this._requestId = 0;
    this._pendingRequestId = null;
    this._unsubs = [];
    this._drag = null;
    this._scrubHandle = null;
    this._frameBatcher = createFrameBatcher((payload) => this._writeDragFrame(payload));
    this._coachmarkEl = null;
    this._scopeOpen = false;
  }

  mount() {
    this._root = createEl('div', 'pb-gss');
    this._render();
    this._container.appendChild(this._root);
    this._wire();
    this._setSelfFromRect(this._rectData);
    this._queryGroup({ preview: false });
    this._maybeShowCoachmark();
  }

  retireEditableIdentity() {
    const drag = this._drag;
    this._drag = null;
    this._frameBatcher.cancel();
    this._scrubHandle?.cancel();
    this._scrubHandle = null;
    if (!drag) return;
    if (drag.useTransient) bridge.send('clear-transient-spacing-css', {});
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
    const overlay = this._panel?._overlay?.getOverlayElement?.();
    overlay?.classList.remove('pb-glass-spacing-dragging');
    if (overlay) overlay.style.cursor = '';
    eventBus.emit('glass:spacing-drag-end');
  }

  destroy() {
    if (this._drag) this._endDrag({ commit: true });
    if (this._scrubHandle) {
      try {
        this._scrubHandle.cancel();
      } catch {}
    }
    for (const off of this._unsubs) off();
    this._unsubs = [];
    eventBus.emit('glass:spacing-targets-clear');
    eventBus.emit('glass:spacing-dim-clear');
    this._widget?.destroy();
    this._widget = null;
    this._root?.remove();
    this._root = null;
  }

  _render() {
    this._root.textContent = '';

    // Selection breadcrumb (Phase 2.8). Only renders when ancestry is available.
    const ancestry = Array.isArray(this._rectData?.ancestry) ? this._rectData.ancestry : [];
    if (ancestry.length > 0) {
      this._breadcrumbEl = createEl('div', 'pb-gss-breadcrumb');
      this._renderBreadcrumb(ancestry);
      this._root.appendChild(this._breadcrumbEl);
    }

    const header = createEl('div', 'pb-gss-header');
    header.appendChild(createEl('span', 'pb-gp-label', 'Spacing'));

    const unit = createEl('div', 'pb-gp-segmented pb-gss-unit');
    for (const value of ['rem', 'px']) {
      const btn = createEl('button', value === this._unit ? 'pb-gp-active' : '', value);
      btn.type = 'button';
      btn.dataset.unit = value;
      unit.appendChild(btn);
    }
    header.appendChild(unit);
    this._root.appendChild(header);

    // Apply-to scope dropdown (custom — supports hover preview + count chip)
    this._scopeEl = createEl('div', 'pb-gss-scope');
    this._scopeButton = createEl('button', 'pb-gss-scope-button');
    this._scopeButton.type = 'button';
    this._scopeButton.setAttribute('aria-haspopup', 'listbox');
    this._scopeButton.setAttribute('aria-expanded', 'false');

    this._scopeButtonLabel = createEl(
      'span',
      'pb-gss-scope-button-label',
      'Apply to: This element'
    );
    this._scopeButtonChevron = createEl('span', 'pb-gss-scope-button-chevron', '▾');
    this._scopeButton.append(this._scopeButtonLabel, this._scopeButtonChevron);

    this._scopeMenu = createEl('div', 'pb-gss-scope-menu');
    this._scopeMenu.setAttribute('role', 'listbox');
    this._scopeMenu.hidden = true;

    this._scopeOptions = [
      { id: 'self', label: 'This element', count: null, disabled: false },
      { id: 'siblings', label: 'Matching siblings', count: 0, disabled: true }
    ];
    this._scopeMenuItems = new Map();
    for (const opt of this._scopeOptions) {
      const item = createEl('button', 'pb-gss-scope-item', '');
      item.type = 'button';
      item.dataset.scope = opt.id;
      item.setAttribute('role', 'option');
      const labelEl = createEl('span', 'pb-gss-scope-item-label', opt.label);
      const countEl = createEl('span', 'pb-gss-scope-item-count', '');
      item.append(labelEl, countEl);
      item.addEventListener('mouseenter', () => this._onScopeOptionHover(opt.id));
      item.addEventListener('mouseleave', () => this._onScopeOptionHover(null));
      item.addEventListener('focus', () => this._onScopeOptionHover(opt.id));
      item.addEventListener('click', () => this._setScope(opt.id));
      this._scopeMenu.appendChild(item);
      this._scopeMenuItems.set(opt.id, { item, labelEl, countEl });
    }
    this._scopeEl.append(this._scopeButton, this._scopeMenu);
    this._root.appendChild(this._scopeEl);

    this._diversityEl = createEl('div', 'pb-gss-diversity');
    this._diversityEl.hidden = true;
    this._root.appendChild(this._diversityEl);

    // Box-model widget host
    this._widgetHost = createEl('div', 'pb-gss-widget-host');
    this._root.appendChild(this._widgetHost);

    this._widget = new BoxModelWidget({
      container: this._widgetHost,
      onEvent: (e) => this._onWidgetEvent(e)
    });
    this._widget.setUnit(this._unit);

    const footer = createEl('div', 'pb-gss-footer');
    const resetAll = createEl('button', 'pb-gss-reset-all', 'Reset all');
    resetAll.type = 'button';
    resetAll.addEventListener('click', () => this._resetAll());
    footer.appendChild(resetAll);
    this._root.appendChild(footer);
  }

  _renderBreadcrumb(ancestry) {
    if (!this._breadcrumbEl) return;
    renderBreadcrumb(this._breadcrumbEl, ancestry, {
      instanceId: this._data.instanceId,
      eid: this._data.eid
    });
  }

  _wire() {
    this._root.addEventListener('click', (e) => {
      const unitBtn = e.target.closest('[data-unit]');
      if (unitBtn) {
        this._unit = unitBtn.dataset.unit || 'rem';
        this._syncUnitButtons();
        this._widget?.setUnit(this._unit);
        return;
      }
    });
    this._root.addEventListener('keydown', (e) => this._onShortcutKeyDown(e));

    this._scopeButton.addEventListener('click', () => this._toggleScopeMenu());
    document.addEventListener(
      'click',
      (this._boundOutside = (e) => {
        if (!this._scopeOpen) return;
        if (this._scopeEl.contains(e.target)) return;
        this._closeScopeMenu();
      }),
      true
    );

    this._on('iframe:element-rect', (payload) => this._onRect(payload));
    this._on('iframe:element-rect-update', (payload) => this._onRect(payload));
    this._on('iframe:style-snapshot', (payload) => this._onStyleSnapshot(payload));
    this._on('iframe:spacing-group-result', (payload) => this._onGroupResult(payload));
    this._on('glass:spacing-badge-pointerdown', (payload) => this._onBadgePointerDown(payload));
  }

  _onShortcutKeyDown(e) {
    if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
    if (e.key === ']' || e.code === 'BracketRight') {
      e.preventDefault();
      e.stopPropagation();
      bridge.send('select-relative-editable', {
        instanceId: this._data.instanceId,
        eid: this._data.eid,
        direction: 'parent'
      });
    } else if (e.key === '[' || e.code === 'BracketLeft') {
      e.preventDefault();
      e.stopPropagation();
      bridge.send('select-relative-editable', {
        instanceId: this._data.instanceId,
        eid: this._data.eid,
        direction: 'child'
      });
    }
  }

  _on(event, fn) {
    this._unsubs.push(eventBus.on(event, fn));
  }

  _syncUnitButtons() {
    for (const btn of this._root.querySelectorAll('[data-unit]')) {
      btn.classList.toggle('pb-gp-active', btn.dataset.unit === this._unit);
    }
  }

  _toggleScopeMenu() {
    if (this._scopeOpen) this._closeScopeMenu();
    else this._openScopeMenu();
  }

  _openScopeMenu() {
    if (this._scopeOpen) return;
    this._scopeOpen = true;
    this._scopeMenu.hidden = false;
    this._scopeButton.setAttribute('aria-expanded', 'true');
    // Pre-warm the group query so the count is fresh when the menu opens.
    this._queryGroup({ preview: false });
  }

  _closeScopeMenu() {
    if (!this._scopeOpen) return;
    this._scopeOpen = false;
    this._scopeMenu.hidden = true;
    this._scopeButton.setAttribute('aria-expanded', 'false');
    if (this._scopeHover) {
      this._scopeHover = null;
      this._refreshHoverPreview();
    }
  }

  _onScopeOptionHover(scopeId) {
    if (scopeId === 'siblings' && !this._groupAvailable) {
      // Still trigger preview so user sees there is no group
    }
    this._scopeHover = scopeId;
    this._refreshHoverPreview();
  }

  _refreshHoverPreview() {
    const effectiveScope = this._scopeHover || this._scope;
    const items = this._itemsForScope(effectiveScope);
    if (effectiveScope === 'siblings' && this._groupAvailable && items.length > 1) {
      eventBus.emit('glass:spacing-targets', {
        targets: items,
        pulse: this._scopeHover === 'siblings' && this._scope !== 'siblings'
      });
      eventBus.emit('glass:spacing-dim-set', { eids: items.map((g) => g.eid).filter(Boolean) });
    } else {
      eventBus.emit('glass:spacing-targets-clear');
      eventBus.emit('glass:spacing-dim-clear');
    }
  }

  _setScope(scopeId) {
    if (scopeId === 'siblings' && !this._groupAvailable) {
      this._closeScopeMenu();
      return;
    }
    const previous = this._scope;
    this._scope = scopeId === 'siblings' ? 'siblings' : 'self';
    this._scopeHover = null;
    this._closeScopeMenu();
    this._updateScopeButtonLabel();
    this._renderScopeOptions();
    if (this._scope === 'siblings') {
      if (this._groupItemsCache?.length > 1) {
        this._groupItems = this._groupItemsCache;
        this._refreshWidget();
        eventBus.emit('glass:spacing-targets', { targets: this._groupItems, pulse: true });
        eventBus.emit('glass:spacing-dim-set', {
          eids: this._groupItems.map((g) => g.eid).filter(Boolean)
        });
        this._maybeRepositionPanelToGroup();
      }
      this._queryGroup({ preview: true });
    } else {
      eventBus.emit('glass:spacing-targets-clear');
      eventBus.emit('glass:spacing-dim-clear');
      this._setSelfFromRect(this._rectData);
      if (previous === 'siblings') this._panel?.positionNear?.(this._rectData?.rect);
    }
  }

  _updateScopeButtonLabel() {
    if (!this._scopeButtonLabel) return;
    const items = this._itemsForScope('siblings');
    if (this._scope === 'siblings' && this._groupAvailable) {
      this._scopeButtonLabel.textContent = `Apply to: Siblings (${items.length})`;
    } else {
      this._scopeButtonLabel.textContent = 'Apply to: This element';
    }
  }

  _renderScopeOptions() {
    for (const opt of this._scopeOptions) {
      const ref = this._scopeMenuItems.get(opt.id);
      if (!ref) continue;
      let count = '';
      let disabled = false;
      if (opt.id === 'siblings') {
        count = this._groupAvailable ? `${this._itemsForScope('siblings').length}` : '0';
        disabled = !this._groupAvailable;
      }
      ref.countEl.textContent = count;
      ref.item.disabled = disabled;
      ref.item.classList.toggle('pb-gss-scope-item-disabled', disabled);
      ref.item.classList.toggle('pb-gss-scope-item-active', this._scope === opt.id);
      if (disabled && opt.id === 'siblings') {
        ref.item.dataset.tooltip = 'No similar siblings detected';
      } else {
        ref.item.dataset.tooltip = '';
      }
    }
  }

  _onRect(payload) {
    if (!payload || payload.eid !== this._data.eid) return;
    if (this._drag) return;
    this._rectData = { ...this._rectData, ...payload };
    if (Array.isArray(payload.ancestry)) {
      this._renderBreadcrumb(payload.ancestry);
    }
    if (this._scope === 'self') {
      this._setSelfFromRect(this._rectData);
    } else {
      // Group rect updates come via spacing-group-result; skip here.
    }
  }

  _onStyleSnapshot(payload) {
    if (!payload || payload.instanceId !== this._data.instanceId || payload.eid !== this._data.eid)
      return;
    if (this._drag) return;
    this._rectData = mergeSpacingSnapshotData(this._rectData, payload);
    if (this._scope === 'self') {
      this._setSelfFromRect(this._rectData);
    } else {
      this._queryGroup({ preview: true });
    }
  }

  _setSelfFromRect(data) {
    const item = currentSpacingItemFromRect(data);
    this._groupItems = item ? [item] : [];
    this._refreshWidget();
  }

  _queryGroup({ preview }) {
    if (!this._data?.eid || !this._data?.instanceId) return;
    const requestId = ++this._requestId;
    this._pendingRequestId = requestId;
    bridge.send('query-spacing-group', {
      eid: this._data.eid,
      instanceId: this._data.instanceId,
      scope: 'siblings',
      requestId,
      elementOverrides: sectionElementOverrides(this._data.instanceId)
    });
    if (!preview && this._scope !== 'siblings') {
      eventBus.emit('glass:spacing-targets-clear');
      eventBus.emit('glass:spacing-dim-clear');
    }
  }

  _onGroupResult(payload) {
    if (!payload || payload.eid !== this._data.eid) return;
    if (this._pendingRequestId && payload.requestId && payload.requestId !== this._pendingRequestId)
      return;
    const items = Array.isArray(payload.items) ? payload.items : [];
    this._groupAvailable = items.length > 1;
    this._groupItemsCache = items;
    if (this._scope === 'siblings' && this._groupAvailable) {
      this._groupItems = items;
    }

    this._renderScopeOptions();
    this._updateScopeButtonLabel();

    if (this._scope !== 'siblings') {
      // Cache the items so hover preview can fire instantly next time.
      this._refreshHoverPreview();
      return;
    }

    if (!this._groupAvailable) {
      this._scope = 'self';
      this._updateScopeButtonLabel();
      this._renderScopeOptions();
      eventBus.emit('glass:spacing-targets-clear');
      eventBus.emit('glass:spacing-dim-clear');
      this._setSelfFromRect(this._rectData);
      return;
    }

    this._refreshWidget();
    eventBus.emit('glass:spacing-targets', { targets: items, pulse: true });
    eventBus.emit('glass:spacing-dim-set', { eids: items.map((g) => g.eid).filter(Boolean) });
    this._maybeRepositionPanelToGroup();
  }

  _itemsForScope(scope) {
    if (scope === 'siblings') {
      if (this._scope === 'siblings') return this._groupItems;
      return this._groupItemsCache && this._groupItemsCache.length
        ? this._groupItemsCache
        : this._groupItems;
    }
    return this._groupItems;
  }

  _refreshWidget() {
    if (!this._widget) return;
    const rootPx = rootFontPx(this._rectData);
    this._widget.setRootPx(rootPx);

    const values = {};
    const mixed = {};
    const diversity = {};
    const auto = {};
    const template = {};
    const negative = {};

    for (const field of SPACING_FIELDS) {
      const isMixed =
        this._scope === 'siblings' && hasMixedSpacingValue(this._groupItems, field, rootPx);
      mixed[field] = isMixed;
      if (isMixed) {
        diversity[field] = diversityForField(this._groupItems, field, rootPx);
        values[field] = '';
        continue;
      }
      const computed = this._groupItems[0]?.computed?.[field];
      const templateSource = this._groupItems[0]?.template?.[field];
      const overrideMap = this._groupItems[0]?.override || {};
      const overrideField = routeSpacingProp(field, this._rectData);
      const overrideValue = overrideMap[overrideField];
      const isAuto =
        String(computed || '').toLowerCase() === 'auto' ||
        String(overrideValue || '').toLowerCase() === 'auto';
      auto[field] = isAuto;
      const px = parseCssLengthPx(computed, rootPx);
      negative[field] = !field.startsWith('padding') && px < -0.5;
      values[field] = computed || '0px';
      template[field] = !overrideValue && isTemplateValue(templateSource || computed);
    }

    this._widget.setValues({ values, mixed, diversity, auto, template, negative });
    this._renderDiversityCallout(mixed, diversity);
  }

  _renderDiversityCallout(mixed, diversity) {
    if (!this._diversityEl) return;
    const sides = SPACING_FIELDS.filter((f) => mixed[f] && diversity[f]?.length > 1);
    if (this._scope !== 'siblings' || sides.length === 0) {
      this._diversityEl.hidden = true;
      this._diversityEl.textContent = '';
      return;
    }
    this._diversityEl.hidden = false;
    this._diversityEl.textContent = '';
    for (const field of sides) {
      const row = createEl('div', 'pb-gss-diversity-row');
      const label = createEl('span', 'pb-gss-diversity-label', this._fieldLabel(field));
      const samples =
        diversity[field].slice(0, 3).join(' · ') +
        (diversity[field].length > 3 ? ` +${diversity[field].length - 3}` : '');
      const value = createEl('span', 'pb-gss-diversity-value', samples);
      row.append(label, value);
      this._diversityEl.appendChild(row);
    }
  }

  _fieldLabel(field) {
    const kind = field.startsWith('padding') ? 'Padding' : 'Margin';
    const side = field.replace(/^[a-z]+/, '');
    return `${kind} ${side}`;
  }

  _targetEids() {
    if (this._scope === 'siblings' && this._groupAvailable) {
      return this._groupItems.map((item) => item.eid).filter(Boolean);
    }
    return [this._data.eid];
  }

  _onWidgetEvent(e) {
    if (!e) return;
    if (e.type === 'side-focus') {
      this._focusedField = e.field;
    } else if (e.type === 'scrub-start') {
      this._beginWidgetScrub(e);
    } else if (e.type === 'edit-commit') {
      this._commitInput(e.field, e.value);
    } else if (e.type === 'arrow-step') {
      this._arrowStep(e.field, e.direction, e.modifiers);
    } else if (e.type === 'reset-side') {
      this._resetField(e.field);
    } else if (e.type === 'side-hover') {
      // Reserved for future tooltip work.
    } else if (e.type === 'link-toggle') {
      // No-op; widget keeps lock state internally.
    }
  }

  _beginWidgetScrub({ field, axis, sign, event, lockedFields }) {
    if (this._drag) return;
    const rootPxValue = rootFontPx(this._rectData);
    const current =
      this._groupItems[0]?.computed?.[field] ||
      spacingForDisplayData(this._rectData)?.[field] ||
      '0px';
    const startPx = parseCssLengthPx(current, rootPxValue);
    const targetEids = this._targetEids();
    const useTransient = targetEids.length > TRANSIENT_CSS_THRESHOLD;
    const overlayEl = this._panel?._overlay?.getOverlayElement?.();
    const zoom =
      Number.isFinite(this._rectData?.zoom) && this._rectData.zoom > 0 ? this._rectData.zoom : 1;

    this._drag = {
      field,
      startPx,
      lockedFields: lockedFields && lockedFields.length ? lockedFields : [field],
      targetEids,
      useTransient
    };

    if (useTransient) {
      bridge.send('set-transient-spacing-css', { rules: [] });
    }

    store.beginTransaction('Adjust spacing');
    document.body.style.userSelect = 'none';
    document.body.style.cursor = axis === 'x' ? 'ew-resize' : 'ns-resize';
    if (overlayEl) {
      overlayEl.classList.add('pb-glass-spacing-dragging');
      overlayEl.style.cursor = document.body.style.cursor;
    }
    eventBus.emit('glass:spacing-drag-start', { field });

    this._scrubHandle = beginScrub({
      event,
      axis,
      startPx,
      zoom,
      sign,
      min: field.startsWith('padding') ? 0 : -999,
      max: 999,
      snap: true,
      onMove: (info) => this._onScrubMove(info),
      onEnd: () => this._endDrag({ commit: true }),
      onCancel: () => this._endDrag({ commit: false })
    });
  }

  _onScrubMove(info) {
    if (!this._drag || !info) return;
    const cssValue = formatCssLength(info.px, this._unit, rootFontPx(this._rectData));
    const fields = this._drag.lockedFields;
    if (info.snapped && this._widget) this._widget.pulseSnap(this._drag.field);
    if (info.modifiers?.fineMode) {
      // No special UX yet; reserved.
    }
    if (info.px <= 0 && this._drag.field.startsWith('padding') && info.delta < 0) {
      this._widget?.flashClampReject(this._drag.field);
    }
    this._frameBatcher.schedule({ cssValue, fields, px: info.px });
    eventBus.emit('glass:spacing-badge-preview', {
      field: this._drag.field,
      value: `${info.px}px`
    });
  }

  _writeDragFrame(payload) {
    if (!this._drag || !payload) return;
    const resolved = resolveSectionByInstance(this._data.instanceId);
    if (!resolved) return;

    if (this._drag.useTransient) {
      this._sendTransientCss(payload.fields, payload.cssValue);
      this._updateLocalComputed(payload.fields, payload.cssValue);
      return;
    }

    const targetEids = this._drag.targetEids;
    const viewport = getResponsiveOverrideViewport();

    for (const field of payload.fields || [this._drag.field]) {
      const prop = routeSpacingProp(field, this._rectData);
      for (const eid of targetEids) {
        applyElementOverrides(
          this._data.instanceId,
          eid,
          { [prop]: payload.cssValue },
          {
            label: 'Adjust spacing',
            source: 'glass-shared-spacing',
            viewport
          }
        );
      }
      this._updateLocalComputed([field], payload.cssValue);
    }
  }

  _sendTransientCss(fields, cssValue) {
    const rules = [];
    const flat = !!isImageInsidePicture(this._rectData);
    for (const eid of this._drag.targetEids) {
      if (!eid) continue;
      const decls = (fields || [this._drag.field]).map((field) => {
        return `${FIELD_TO_CSS[field]}: ${cssValue}`;
      });
      rules.push({
        eid,
        declarations: decls.join('; '),
        targetPicture: flat
      });
    }
    bridge.send('set-transient-spacing-css', { rules });
  }

  _updateLocalComputed(fields, cssValue) {
    if (!Array.isArray(fields)) return;
    const value = cssValue || '0px';
    for (const item of this._groupItems) {
      if (!item.computed) item.computed = {};
      for (const field of fields) {
        item.computed[field] = value;
      }
    }
    this._refreshWidget();
  }

  _commitInput(field, inputValue) {
    const trimmed = String(inputValue || '')
      .trim()
      .toLowerCase();
    if (trimmed === 'auto' && !field.startsWith('padding')) {
      this._applyField(field, 'auto', { label: `Set ${field} auto` });
      return;
    }
    const cssValue = parseInputCssValue(inputValue, this._unit);
    if (!cssValue && inputValue.trim() === '') {
      this._resetField(field);
      return;
    }
    this._applyField(field, cssValue, { label: `Adjust ${field}` });
  }

  _arrowStep(field, direction, modifiers = {}) {
    const rootPxValue = rootFontPx(this._rectData);
    const current =
      this._groupItems[0]?.computed?.[field] ||
      spacingForDisplayData(this._rectData)?.[field] ||
      '0px';
    const startPx = parseCssLengthPx(current, rootPxValue);
    const ladder = readDesignTokenLadder();
    let nextPx;
    if (modifiers.stepMode) {
      nextPx = nextTokenStep(startPx, ladder, direction);
    } else if (modifiers.fineMode) {
      nextPx = startPx + 0.1 * direction;
    } else {
      nextPx = startPx + direction;
    }
    if (field.startsWith('padding') && nextPx < 0) nextPx = 0;
    const cssValue = formatCssLength(nextPx, this._unit, rootPxValue);
    this._applyField(field, cssValue, { label: `Adjust ${field}` });
  }

  _resetField(field) {
    this._applyField(field, '', { label: `Reset ${field}`, deleteOnly: true });
  }

  _resetAll() {
    const targetEids = this._targetEids();
    const resolved = resolveSectionByInstance(this._data.instanceId);
    if (!resolved) return;
    const propsToClear = isImageInsidePicture(this._rectData)
      ? PICTURE_SPACING_PROPS
      : SPACING_FIELDS;
    const viewport = getResponsiveOverrideViewport();

    store.beginTransaction('Reset spacing');
    for (const eid of targetEids) {
      const changes = Object.fromEntries(propsToClear.map((prop) => [prop, null]));
      applyElementOverrides(this._data.instanceId, eid, changes, {
        label: 'Reset spacing',
        source: 'glass-shared-spacing',
        viewport
      });
    }
    store.commitTransaction();
    this._afterWrite();
    this._postCommitToast(targetEids.length, 'Spacing reset');
    this._flashTargets();
  }

  _applyField(field, cssValue, { label, deleteOnly } = {}) {
    const prop = routeSpacingProp(field, this._rectData);
    const targetEids = this._targetEids();
    const resolved = resolveSectionByInstance(this._data.instanceId);
    if (!resolved) return;

    const valueToWrite = deleteOnly ? '' : cssValue;
    const viewport = getResponsiveOverrideViewport();

    store.beginTransaction(label || `Adjust ${field}`);
    for (const eid of targetEids) {
      applyElementOverrides(
        this._data.instanceId,
        eid,
        { [prop]: valueToWrite },
        {
          label: label || `Adjust ${field}`,
          source: 'glass-shared-spacing',
          viewport
        }
      );
    }
    store.commitTransaction();

    this._updateLocalComputed([field], valueToWrite);
    this._afterWrite();
    this._postCommitToast(
      targetEids.length,
      deleteOnly
        ? `Reset ${this._fieldLabel(field).toLowerCase()}`
        : `${this._fieldLabel(field)} ${valueToWrite || 'cleared'}`
    );
    this._flashTargets();
  }

  _afterWrite() {
    bridge.send('query-element-rect', {});
    if (this._scope === 'siblings') this._queryGroup({ preview: true });
  }

  _flashTargets() {
    eventBus.emit('glass:spacing-targets-flash');
  }

  _postCommitToast(targetCount, message) {
    if (this._scope !== 'siblings' || targetCount <= 1) return;
    const text = `${message} on ${targetCount} elements`;
    _ensureUndoToast().then((fn) => {
      try {
        fn(text);
      } catch {}
    });
  }

  _onBadgePointerDown(payload) {
    if (!payload || payload.data?.eid !== this._data.eid) return;
    const field = payload.field;
    if (!SPACING_FIELDS.includes(field)) return;
    // Treat badge drag identically to a widget-side drag.
    this._beginWidgetScrub({
      field,
      axis: FIELD_AXIS[field] || 'y',
      sign: FIELD_DRAG_SIGN[field] || 1,
      event: {
        clientX: payload.clientX,
        clientY: payload.clientY,
        button: 0,
        preventDefault: () => {},
        stopPropagation: () => {}
      },
      lockedFields: this._widget?._lockedFieldsFor?.(field) || [field]
    });
    // beginScrub needs a real PointerEvent for its document listeners — synthesize via current event mocking
    // Workaround: register move listeners ourselves by using the pointermove from the document.
    // Since beginScrub already attaches pointermove listeners, the synthesized event is only used for
    // initial coordinate capture. The actual move tracking uses the live pointermove events.
  }

  _endDrag({ commit }) {
    if (!this._drag) return;
    this._frameBatcher.flush();
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
    const overlayEl = this._panel?._overlay?.getOverlayElement?.();
    if (overlayEl) {
      overlayEl.classList.remove('pb-glass-spacing-dragging');
      overlayEl.style.cursor = '';
    }
    if (this._drag.useTransient) {
      bridge.send('clear-transient-spacing-css', {});
      // Now persist the final state to the store.
      this._persistDragFinal();
    }
    if (commit) {
      store.commitTransaction();
    } else {
      store.abortTransaction();
      eventBus.emit('theme:override-changed');
    }
    const wasSiblings = this._scope === 'siblings';
    const finalDrag = this._drag;
    this._drag = null;
    this._scrubHandle = null;
    eventBus.emit('glass:spacing-drag-end');
    bridge.send('query-element-rect', {});
    if (wasSiblings) this._queryGroup({ preview: true });
    if (commit) {
      this._postCommitToast(
        finalDrag.targetEids.length,
        `${this._fieldLabel(finalDrag.field)} updated`
      );
      this._flashTargets();
      this._dismissCoachmark();
    }
  }

  _persistDragFinal() {
    if (!this._drag) return;
    const resolved = resolveSectionByInstance(this._data.instanceId);
    if (!resolved) return;
    const targetEids = this._drag.targetEids;
    const viewport = getResponsiveOverrideViewport();
    // Use the current local-computed value as the canonical final value.
    for (const field of this._drag.lockedFields) {
      const value = this._groupItems[0]?.computed?.[field] || '';
      const prop = routeSpacingProp(field, this._rectData);
      for (const eid of targetEids) {
        applyElementOverrides(
          this._data.instanceId,
          eid,
          { [prop]: value },
          {
            label: 'Adjust spacing',
            source: 'glass-shared-spacing',
            viewport
          }
        );
      }
    }
  }

  _maybeRepositionPanelToGroup() {
    if (!this._panel || typeof this._panel.positionNear !== 'function') return;
    if (!this._groupAvailable || this._groupItems.length === 0) return;
    let minLeft = Infinity,
      minTop = Infinity,
      maxRight = -Infinity,
      maxBottom = -Infinity;
    for (const item of this._groupItems) {
      const r = item.rect;
      if (!r) continue;
      minLeft = Math.min(minLeft, r.left);
      minTop = Math.min(minTop, r.top);
      maxRight = Math.max(maxRight, r.left + r.width);
      maxBottom = Math.max(maxBottom, r.top + r.height);
    }
    if (!Number.isFinite(minLeft)) return;
    const groupRect = {
      left: minLeft,
      top: minTop,
      width: maxRight - minLeft,
      height: maxBottom - minTop
    };
    this._panel.positionNear(groupRect, { group: true, allowMiniBar: true });
  }

  _maybeShowCoachmark() {
    if (readCoachmarkSeen()) return;
    if (!this._widgetHost) return;
    const tip = createEl('div', 'pb-gss-coachmark', '');
    const text = createEl('span', 'pb-gss-coachmark-text', 'Drag any side to adjust');
    const dismiss = createEl('button', 'pb-gss-coachmark-dismiss', '✕');
    dismiss.type = 'button';
    dismiss.setAttribute('aria-label', 'Dismiss tip');
    dismiss.addEventListener('click', () => this._dismissCoachmark());
    tip.append(text, dismiss);
    this._widgetHost.appendChild(tip);
    this._coachmarkEl = tip;
  }

  _dismissCoachmark() {
    if (!this._coachmarkEl) return;
    this._coachmarkEl.classList.add('pb-gss-coachmark-dismiss-anim');
    setTimeout(() => {
      this._coachmarkEl?.remove();
      this._coachmarkEl = null;
    }, 200);
    writeCoachmarkSeen();
  }
}
